/**
 * Client-side marker that this device has (or declined) a passkey. Drives the
 * enrolment offer's cadence here and the step-0 passkey login (BC-4832).
 *
 * Holds the enrolling member's ID and display first name, and nothing else that
 * identifies them — no email, no token, no session material. The ID is here
 * because a passkey belongs to an account while this hint belongs to a browser:
 * without it, one person enrolling suppressed the offer for every other account
 * ever used on that browser, since `enrolled: true` read as "this person is
 * covered" when it only ever meant "someone is".
 *
 * Anything added here is readable by any script on the origin, so it earns its
 * place by being useless to an attacker who already has it.
 */
export interface PasskeyDeviceHint {
    /** A passkey was registered from this browser profile. */
    enrolled: boolean;
    /**
     * The member the credential belongs to. Absent on hints written before the
     * field existed, which are treated as "unknown owner" rather than re-asking
     * everyone who has already enrolled.
     */
    memberID?: number;
    /** Display name for the step-0 "Welcome back, {firstName}" screen. */
    firstName?: string;
    /** Relying party the credential is scoped to (host without www). */
    rpId?: string;
    enrolledAt?: number;
    /** Set when the user skipped the enrolment offer. */
    skippedAt?: number;
    /** Auths seen since the last skip (drives the re-offer cadence). */
    authsSinceSkip?: number;
}

const STORAGE_KEY = 'ap-passkey-hint';

/**
 * Re-offer cadence after a skip — provisional until product decides
 * (recorded in epics/BC-4826-login-signup-modal-revamp/bc-4831-passkey-plan.md):
 * offer again after 30 days, or after every 3rd auth, whichever comes first.
 */
const REOFFER_AFTER_MS = 30 * 24 * 60 * 60 * 1000;
const REOFFER_AFTER_AUTHS = 3;

export const readPasskeyDeviceHint = (): PasskeyDeviceHint | null => {
    if (typeof window === 'undefined') return null;
    try {
        const raw = window.localStorage.getItem(STORAGE_KEY);
        if (!raw) return null;
        const parsed: unknown = JSON.parse(raw);
        if (typeof parsed !== 'object' || parsed === null) return null;
        return parsed as PasskeyDeviceHint;
    } catch {
        return null;
    }
};

const writePasskeyDeviceHint = (hint: PasskeyDeviceHint) => {
    if (typeof window === 'undefined') return;
    try {
        window.localStorage.setItem(STORAGE_KEY, JSON.stringify(hint));
    } catch {
        // Storage full / blocked — the offer simply reappears next time.
    }
};

export const markPasskeyEnrolled = (details: {
    /** Whose credential this is. Omitted only when the session is not readable. */
    memberID?: number;
    firstName?: string;
    rpId?: string;
}) => {
    writePasskeyDeviceHint({
        enrolled: true,
        // Recording 0 would be worse than recording nothing: an unknown owner
        // keeps suppressing, while a guest ID never matches the member who
        // comes back, so the ask would return on every login for ever.
        memberID: realMemberID(details.memberID),
        firstName: details.firstName || undefined,
        rpId: details.rpId || undefined,
        enrolledAt: Date.now(),
    });
};

export const markPasskeyOfferSkipped = () => {
    const current = readPasskeyDeviceHint();
    writePasskeyDeviceHint({
        ...(current ?? { enrolled: false }),
        // `enrolled` is carried, never forced down. A skip means "not now", and
        // it can reach here after a ceremony has already registered a
        // credential: the ask holds its success frame before resolving, and
        // whoever walks away during that frame has still enrolled. Claiming
        // otherwise would take away a passkey the authenticator really holds —
        // and with it the step-0 login — until the cadence happened to offer
        // again. A hint that has never been enrolled is unaffected.
        skippedAt: Date.now(),
        authsSinceSkip: 0,
    });
};

/**
 * A member, or `undefined` for "cannot tell".
 *
 * The logged-out user is `{ __typename: 'Guest', ID: 0 }`, so a caller reading
 * `user.ID` hands us **0** — not `undefined` — for every auth whose session has
 * not revalidated yet, which is most of them. Zero is not a member. Read as one
 * it is a member who is never the one that enrolled, so every stored enrolment
 * looked like somebody else's and the ask came back to people who already had a
 * passkey on the device. Normalising here rather than at each call site keeps
 * the next caller from reintroducing it.
 */
const realMemberID = (memberID: number | undefined): number | undefined =>
    memberID !== undefined && memberID > 0 ? memberID : undefined;

/**
 * Does the stored enrolment belong to somebody other than the member who just
 * authenticated? Unknown on either side means "cannot tell", which leaves the
 * enrolment suppressing as it did before the ID was recorded.
 */
const isSomeoneElses = (
    hint: PasskeyDeviceHint,
    memberID: number | undefined,
): boolean => {
    const member = realMemberID(memberID);
    return (
        member !== undefined &&
        hint.memberID !== undefined &&
        hint.memberID !== member
    );
};

/**
 * Should the post-auth enrolment offer show on this device? Callers still gate
 * on the `auth_modal_2026` flag and platform-authenticator support. Counts the
 * auth against the skip cadence when suppressed by a recent skip.
 *
 * An existing enrolment only suppresses the offer for the member who made it.
 * `memberID` names who just authenticated; `isNewAccount` covers a sign-up,
 * where the account exists but the client has not seen its ID yet. A hint
 * written before `memberID` existed has an unknown owner and keeps suppressing,
 * so nobody who already enrolled gets asked again.
 */
export const shouldOfferPasskeyEnrolment = ({
    memberID,
    isNewAccount = false,
}: { memberID?: number; isNewAccount?: boolean } = {}): boolean => {
    const hint = readPasskeyDeviceHint();
    if (!hint) return true;
    if (hint.enrolled && !isNewAccount && !isSomeoneElses(hint, memberID)) {
        return false;
    }
    if (!hint.skippedAt) return true;

    const expired = Date.now() - hint.skippedAt >= REOFFER_AFTER_MS;
    const auths = (hint.authsSinceSkip ?? 0) + 1;
    if (expired || auths >= REOFFER_AFTER_AUTHS) {
        return true;
    }

    writePasskeyDeviceHint({ ...hint, authsSinceSkip: auths });

    return false;
};
